// src/store.js  –  Central localStorage data store
// Admin writes here; AppContext reads and exposes to the whole app.
// Changes are also persisted to the backend API so they survive across
// browsers and devices. When REACT_APP_API is unset the API is assumed to be
// co-located on the same origin (Vercel serverless functions under /api).

const API = (process.env.REACT_APP_API || '').trim();   // e.g. https://xyz.up.railway.app (or '' for same-origin /api)
const ADMIN_SECRET = (process.env.REACT_APP_ADMIN_SECRET || 'zinga-admin-secret-2024').trim();

export const KEYS = {
  products:      'zinga_products',
  hero:          'zinga_hero',
  dealers:       'zinga_dealers',
  resources:     'zinga_resources',
  advantages:    'zinga_advantages',
  navItems:      'zinga_nav',
  settings:      'zinga_settings',
  stats:         'zinga_stats',
  footer:        'zinga_footer',
  about:         'zinga_about',
  gem:           'zinga_gem',
  howItWorks:     'zinga_how_it_works',
  companyTrust:   'zinga_company_trust',
  contactCTA:     'zinga_contact_cta',
  homeShowcase:   'zinga_home_showcase',
  homeVideos:     'zinga_home_videos',
  otherProducts:  'zinga_other_products',
  language:       'zinga_language',
};

export const storeGet = (key) => {
  try { const v = localStorage.getItem(KEYS[key]); return v ? JSON.parse(v) : null; }
  catch { return null; }
};

export const storeSet = (key, value) => {
  try {
    localStorage.setItem(KEYS[key], JSON.stringify(value));
    window.dispatchEvent(new CustomEvent('zinga-store-update', { detail: { key } }));
    // Also persist to server so changes are visible across all browsers/devices.
    // When REACT_APP_API is unset, the API is co-located on the same origin
    // (Vercel serverless functions under /api), so a relative path is used.
    fetch(`${API}/api/data/${key}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'x-admin-secret': ADMIN_SECRET },
      body: JSON.stringify(value),
    }).catch(() => {}); // fire & forget — localStorage update is immediate
  } catch (e) { console.error('Store write failed', e); }
};

export const storeGetOrDefault = (key, def) => storeGet(key) ?? def;

// Fetch the full server store and hydrate localStorage + context.
// Call this once on app startup. When REACT_APP_API is unset, the API is
// co-located on the same origin (Vercel /api), so a relative path is used.
export const hydrateFromServer = async () => {
  try {
    const res = await fetch(`${API}/api/data`);
    if (!res.ok) return;
    const data = await res.json();
    let changed = false;
    Object.entries(data).forEach(([key, value]) => {
      if (KEYS[key]) {
        const newVal = JSON.stringify(value);
        const oldVal = localStorage.getItem(KEYS[key]);
        if (newVal !== oldVal) {
          localStorage.setItem(KEYS[key], newVal);
          changed = true;
          window.dispatchEvent(new CustomEvent('zinga-store-update', { detail: { key } }));
        }
      }
    });
    return changed;
  } catch { /* server not available, use localStorage */ }
};

// ── Defaults ──────────────────────────────────────────────────────────────

export const DEF_PRODUCTS = [
  { id: 'zingalu-5kg',   name: 'Zingaalu - Film Galvanizing with Hot Dip Finish (silver) - 5kg',      price: '10,500.00', image: 'https://static.wixstatic.com/media/ab29c6_1cfbeb841d98435c83bf5cab53c0979f~mv2.jpeg/v1/fill/w_500,h_350,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/WhatsApp%20Image%202021-12-24%20at%209_35_52%20AM.jpeg', category: 'Zingaalu',   description: '' },
  { id: 'zingalu-spray', name: 'Zingaalu Spray 500 ml with Hot Dip Galvanised Finish',                 price: '1,500.00',  image: 'https://static.wixstatic.com/media/4c89a3_28890cf2e7714736892fa24f9cb063ca~mv2.jpg/v1/fill/w_500,h_350,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/ZINGA%20SPRAY.jpg',                                                                                                  category: 'Zingaalu',   description: '' },
  { id: 'zingaspray',    name: 'Zingaspray - Zinc Galvanising Spray (500ml) - Cold/Film Galvanizing',  price: '1,500.00',  image: 'https://static.wixstatic.com/media/4c89a3_28890cf2e7714736892fa24f9cb063ca~mv2.jpg/v1/fill/w_500,h_350,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/ZINGA%20SPRAY.jpg',                                                                                                  category: 'Accessories', description: '' },
  { id: 'zinga',         name: 'Zinga - Film Galvanising System (1kg / 5kg) - 96% Zinc',              price: '2,100.00',  image: 'https://static.wixstatic.com/media/4c89a3_8977ab9f4607449bbb69bbce437f203c~mv2.jpg/v1/fill/w_500,h_350,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/Zinga.jpg',                                                                                                           category: 'Zinga',       description: '' },
  { id: 'zingasolv',     name: 'Zingasolv - Thinner for Zinga - Film Galvanizing (1 and 5 Litres)',   price: '750.00',    image: 'https://static.wixstatic.com/media/4c89a3_8977ab9f4607449bbb69bbce437f203c~mv2.jpg/v1/fill/w_500,h_350,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/Zinga.jpg',                                                                                                           category: 'Accessories', description: '' },
  { id: 'alu-zm',        name: 'Alu ZM - Aluminium Top Coat for Zinga & HDG Structures (1 Lit)',      price: '1,475.00',  image: 'https://static.wixstatic.com/media/4c89a3_eda7113dde4d438bac6f28350a1dc838~mv2.jpg/v1/fill/w_500,h_350,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/AQUAZINGA.jpg',                                                                                                       category: 'Topcoats',    description: '' },
  { id: 'alu-spray',     name: 'Alu Spray - Aluminium Aerosol Spray for Touch-up (500 ml)',           price: '1,534.00',  image: 'https://static.wixstatic.com/media/4c89a3_28890cf2e7714736892fa24f9cb063ca~mv2.jpg/v1/fill/w_500,h_350,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/ZINGA%20SPRAY.jpg',                                                                                                  category: 'Topcoats',    description: '' },
  { id: 'zingatarfree',  name: 'Zingatarfree - For structures immersed in soil and Salt Water (5 Lit)', price: '11,210.00', image: 'https://static.wixstatic.com/media/4c89a3_b8a9797979ea4521b38e1ff94b8957b9~mv2.jpg/v1/fill/w_500,h_350,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/ZINGATARFREE.jpg',                                                                                                category: 'Specialty',   description: '' },
  { id: 'aquazinga',     name: 'AquaZinga - 100% Water Based Anti-Corrosive System (10 kg)',          price: '4,200.00',  image: 'https://static.wixstatic.com/media/4c89a3_eda7113dde4d438bac6f28350a1dc838~mv2.jpg/v1/fill/w_500,h_350,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/AQUAZINGA.jpg',                                                                                                       category: 'AquaZinga',   description: '' },
  { id: 'zinga-1kg',     name: 'Zinga - Film Galvanising System (1 kg) - 96% Zinc',                  price: '560.00',    image: 'https://static.wixstatic.com/media/4c89a3_8977ab9f4607449bbb69bbce437f203c~mv2.jpg/v1/fill/w_500,h_350,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/Zinga.jpg',                                                                                                           category: 'Zinga',       description: '' },
  { id: 'zingalu-1kg',   name: 'Zingalu - Organic Zinc Rich Coating (1 kg)',                          price: '2,650.00',  image: 'https://static.wixstatic.com/media/ab29c6_1cfbeb841d98435c83bf5cab53c0979f~mv2.jpeg/v1/fill/w_500,h_350,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/WhatsApp%20Image%202021-12-24%20at%209_35_52%20AM.jpeg', category: 'Zingaalu',    description: '' },
  { id: 'zinga-5kg',     name: 'Zinga - Film Galvanising System (5 kg) - 96% Zinc',                  price: '2,100.00',  image: 'https://static.wixstatic.com/media/4c89a3_8977ab9f4607449bbb69bbce437f203c~mv2.jpg/v1/fill/w_500,h_350,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/Zinga.jpg',                                                                                                           category: 'Zinga',       description: '' },
];

export const DEF_HERO = [
  { id: 1, img: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1600&h=700&fit=crop&q=80', tag: 'Proven Protection',   title: 'ZINGA\u00ae Cold Galvanising',         caption: 'Zinga applied over Foot over Bridges under MMRDA, on Western Express Highway', btn1Text: 'Explore Products', btn1Link: '/products', btn2Text: 'Get a Quote', btn2Link: '/contact' },
  { id: 2, img: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1600&h=700&fit=crop&q=80', tag: '96% Zinc in Dry Film', title: 'Superior Corrosion Protection', caption: 'Cold galvanising applied on steel structures at industrial sites worldwide',       btn1Text: 'Explore Products', btn1Link: '/products', btn2Text: 'Get a Quote', btn2Link: '/contact' },
  { id: 3, img: 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=1600&h=700&fit=crop&q=80', tag: 'Single Component',   title: 'Galvanisation As Easy As Paint', caption: 'Zinga provides galvanisation on your structure as easily as applying paint',     btn1Text: 'Explore Products', btn1Link: '/products', btn2Text: 'Get a Quote', btn2Link: '/contact' },
];

export const DEF_DEALERS = [
  { id: 1, region: 'Maharashtra & Goa',            name: 'Arham Agencies',               address: '24, Champaklal Industrial Estate, Ground Floor, Next to MTNL Exchange, Sion (East) Mumbai 400022, Maharashtra', rep: 'Mr. Nehul Sanghavi, Partner', phone: '+91 98212 30943', email: 'nehul@arhamagencies.com',    altEmail: '',                      website: 'www.arhamagencies.com', websiteUrl: 'http://www.arhamagencies.com/' },
  { id: 2, region: 'Tamil Nadu, Kerala & Karnataka', name: 'Nutek Engineering',           address: 'No. 3 Agananuru, 1st Cross Street, Srisakthi Nagar, Thurumullaivoyal, Avadi, Chennai - 600 062',                  rep: 'Mr. Bijoy',                   phone: '+91 70105 27886', email: 'bijo@nutek-eng.com',           altEmail: 'info@nutek-eng.com',    website: 'www.nutek-eng.com',     websiteUrl: 'http://www.nutek-eng.com/' },
  { id: 3, region: 'Madhya Pradesh',               name: 'Technofire Engineers Pvt. Ltd', address: 'EB 178, Scheme 94, Service Road, Opp Bombay Hospital, Indore - 452010',                                          rep: 'Shri. Ajitesh Sharma / Shri. Balkrishna Suryavanshi', phone: '+91 9685085200 / 8989549070', email: 'info@technofireengineers.com', altEmail: 'marketing@technofireengineers.com', website: '', websiteUrl: '' },
  { id: 4, region: 'Gujarat',                      name: 'M/s Rucha Enterprises',         address: '305, Cascade Complex, Nizampura Main Road, Chhani Jakat Naka, Vadodara - 390024, Gujarat',                      rep: 'Shri. Manoj Shah / Shri. Ranjit Raj', phone: '0265-2774294, 9824250796', email: 'zinga@polycoates.com',      altEmail: '',                      website: '', websiteUrl: '' },
  { id: 5, region: 'Jharkhand & Orissa',           name: 'Sangeeta Enterprises',          address: 'Tibarewal Nagar, N.H 16, Jagatpur, Cuttack - 754021, Orissa',                                                   rep: 'Vinay Kumar',                 phone: 'Mr. Bijoy',               email: 'golden_commercial@hotmail.com', altEmail: '',                  website: '', websiteUrl: '' },
  { id: 6, region: 'West Bengal',                  name: 'M/s Esdee Electromill Pvt. Ltd', address: '350, N.S.C. Bose Road, Kolkata - 700047, West Bengal',                                                        rep: 'Mr. Sujoy Sikder, Director',  phone: '+91 98310 06454',         email: 'esdeemill@gmail.com',        altEmail: '',                      website: '', websiteUrl: '' },
  { id: 7, region: 'Telangana & Andhra Pradesh',   name: 'Global Aqua Technologies',      address: '#D-306, Smart City, Shaktinagar, LB Nagar, Hyderabad 500074',                                                   rep: 'Shri. B. Srinivas Rao',       phone: '+91 93939 39362',         email: 'globalaqua1@gmail.com',      altEmail: '',                      website: '', websiteUrl: '' },
];

export const DEF_RESOURCES = {
  bridges: [
    { id: 1,  title: 'Rumst - Belgium - Bridge Steel Structure',                        url: 'https://www.zinga.johngalt.in/_files/ugd/4c89a3_d64ae119ab00436fb66909104473856f.pdf' },
    { id: 2,  title: 'Canada - Confederation Bridge Steel Structures',                  url: 'https://www.zinga.johngalt.in/_files/ugd/4c89a3_e369cff1bb4e40d393c950d090d8d14f.pdf' },
    { id: 3,  title: 'Blanice - Czech Republic - River Bridge Steel Structures',        url: 'https://www.zinga.johngalt.in/_files/ugd/4c89a3_093102718a2f4d37a0274f72a9bfecd8.pdf' },
    { id: 4,  title: 'Norway - Finnmark Bridge Steel Structures',                       url: 'https://www.zinga.johngalt.in/_files/ugd/4c89a3_e5a1806cda124d10b5ebede3774fed72.pdf' },
    { id: 5,  title: 'Thailand - Thai-Belgian Bridge - Structural Steels',              url: 'https://www.zinga.johngalt.in/_files/ugd/4c89a3_775207c7b8724e4f906567ad7b1041ac.pdf' },
    { id: 6,  title: 'Ghent - Belgium - Keizer Bridge Steel Structures',                url: 'https://www.zinga.johngalt.in/_files/ugd/4c89a3_e8f890d5ec0149309945e36cba9bc175.pdf' },
    { id: 7,  title: 'Canada - Overlander Bridge Steel Structures',                     url: 'https://www.zinga.johngalt.in/_files/ugd/4c89a3_51910cdc6d00491a8ac3deccc43a9675.pdf' },
    { id: 8,  title: 'Hamburg - Germany - Tangstedt Bridge Steel Structures',           url: 'https://www.zinga.johngalt.in/_files/ugd/4c89a3_1a4e7237e466456e87441ed32d799314.pdf' },
    { id: 9,  title: 'Norway - Hell Bridge Steel Structures',                           url: 'https://www.zinga.johngalt.in/_files/ugd/4c89a3_19dac014ab33469cbdbdd839db116958.pdf' },
    { id: 10, title: 'Taiwan - Jhong Jheng Overpass Bridge - Steel Structures',         url: 'https://www.zinga.johngalt.in/_files/ugd/4c89a3_0750e517742e4e078216a327f4fff4e2.pdf' },
    { id: 11, title: 'Melle - Belgium - Bridge Steel Structures',                       url: 'https://www.zinga.johngalt.in/_files/ugd/4c89a3_e335612a2e824e69aa58a590704fcfa0.pdf' },
    { id: 12, title: 'Beijing - China - New Shougang Bridge Steel Structures',          url: 'https://www.zinga.johngalt.in/_files/ugd/4c89a3_3d5949a4b1bc4916bd44bfd9c5c5685d.pdf' },
    { id: 13, title: 'Myanmar - TOTAL Bridge USB 100B - Structural Steel',              url: 'https://www.zinga.johngalt.in/_files/ugd/4c89a3_7ffcf4ef924645508888ea9b716ed6d0.pdf' },
    { id: 14, title: 'Norway - Kalvoya Bridge Steel Structures',                        url: 'https://www.zinga.johngalt.in/_files/ugd/4c89a3_6582e93d185345e48b8644bf59854e65.pdf' },
    { id: 15, title: 'Afsnee - Belgium - Wegenfonds Bridge Steel Structures',           url: 'https://www.zinga.johngalt.in/_files/ugd/4c89a3_d5efedf7d2064213a0738e95df8f64f0.pdf' },
    { id: 16, title: 'China - Huangzhou Bay Bridge (Rebars)',                           url: 'https://www.zinga.johngalt.in/_files/ugd/4c89a3_78a187cf36c54da6ad64eabb3b2ff1dd.pdf' },
    { id: 17, title: 'China - Monorail Project - Rebars & Other Structures',            url: 'https://www.zinga.johngalt.in/_files/ugd/4c89a3_9891ffd7c5364d4faee6b3e77960ec89.pdf' },
    { id: 18, title: 'Thailand - Phutta Yodfa Bridge - Structural Steels',              url: 'https://www.zinga.johngalt.in/_files/ugd/4c89a3_e116cb3f32f645aaba24ae2c11665513.pdf' },
  ],
  food: [
    { id: 1, title: 'Charleroi - Belgium - Inside Slaughter House & Cooling Chambers', url: 'https://www.zinga.johngalt.in/_files/ugd/4c89a3_81b594c6116142e38d9164aabaaf9b02.pdf' },
    { id: 2, title: 'Thailand - Red Bull - Inside of Storage Tanks',                   url: 'https://www.zinga.johngalt.in/_files/ugd/4c89a3_a5a1115e312a4d1d84b1400a068b35cc.pdf' },
    { id: 3, title: 'Brazil - Water Reservoir - Inside & Outside',                     url: 'https://www.zinga.johngalt.in/_files/ugd/4c89a3_552569d32a6c42588e243ae7ba0fbc78.pdf' },
    { id: 4, title: 'United Kingdom - Inside Water Storage Tank',                      url: 'https://www.zinga.johngalt.in/_files/ugd/4c89a3_de55ea201a31428a9a41fc88fb1a45e7.pdf' },
    { id: 5, title: 'Finland - Cattle And Pigfarm - Recoating on Galvanized Structures', url: 'https://www.zinga.johngalt.in/_files/ugd/4c89a3_00cfa11fd9234deead4354f7ffcfe58e.pdf' },
    { id: 6, title: 'Ghana - Roofing - Barry Callebaut (Chocolate Company)',           url: 'https://www.zinga.johngalt.in/_files/ugd/4c89a3_4619154a11a64880b894c06762ff607d.pdf' },
  ],
  railways: { title: 'ZINGA on Railway Transport', desc: 'Comprehensive presentation covering ZINGA\u00ae applications on Indian Railways, railway bridges, rolling stock and track infrastructure.', url: 'https://www.zinga.johngalt.in/_files/ugd/4c89a3_3d648429d2514c35891b619f3a3b7565.pptx?dn=ZINGA%20on%20Railway%20Transport%20-PPT.EN.pptx' },
};

export const DEF_ADVANTAGES = [
  { id: 1,  num: '01', title: 'Guaranteed 96% Zinc in Dry Film Thickness',       body: 'ZINGA has a zinc content of 96% in the dry layer. The zinc is pure to 99.995%. In order to obtain a real cathodic protection you need at least 92% of zinc in the dry layer.' },
  { id: 2,  num: '02', title: 'Ease of Application',                              body: 'Apply using a brush, roller or painting gloves. Can also be sprayed with a pistol, and it can be applied in a bath. Can be applied on site by non-professionals.' },
  { id: 3,  num: '03', title: 'Application Over a Wide Range of Weather Conditions', body: 'Can be applied over a damp surface. ZINGA can be applied at very high or low temperatures (up to 45\u00b0C and down to -15\u00b0C).' },
  { id: 4,  num: '04', title: 'Quick Drying Time',                                body: 'A new layer of ZINGA can be applied after 1 hour. Topcoats can be applied after 4 to 24 hours.' },
  { id: 5,  num: '05', title: 'No Cracking or Peeling',                           body: 'ZINGA does not peel off and is not brittle. In case of mechanical damage the ZINGA layer will be compressed or squashed, but it will not crack due to its flexibility.' },
  { id: 6,  num: '06', title: 'Rechargeability',                                  body: 'Each new layer of ZINGA blends perfectly with the previous one, creating one single homogeneous layer with no risk of accumulation that could cause peeling.' },
  { id: 7,  num: '07', title: 'Weldable',                                         body: 'Welding can be done over a layer of ZINGA without peeling or burning off the active cathodic layer.' },
  { id: 8,  num: '08', title: 'Application over Metallised or HDG Structures',    body: 'Structures that have been metallised or galvanised by hot-dip can be recharged with ZINGA after a certain period.' },
  { id: 9,  num: '09', title: 'Protection with Specialized Binder',               body: 'ZINGA is based on protected zinc. The zinc particles are protected by a special resin that allows the galvanic couple formation but also gives additional protection.' },
  { id: 10, num: '10', title: 'Atomised Zinc is the Best in the World',           body: 'The atomisation process provides unique qualities and a very high zinc purity — one of the reasons why ZINGA has no true competitor in the world.' },
  { id: 11, num: '11', title: 'Compatible as the Universal Primer',               body: 'ZINGA can be topcoated by epoxy, polyurethane, vinyl and micaceous iron oxides. A duplex system more than doubles the lifetime of ZINGA.' },
  { id: 12, num: '12', title: 'Non Toxic / Potable',                              body: 'ZINGA is composed of non-toxic elements according to European Standards and can be used in contact with potable water (BS 6920).' },
];

export const DEF_NAV = [
  { id: 1, label: 'Home',       href: '/' },
  { id: 2, label: 'Products',   href: '/products' },
  { id: 3, label: 'GeM Portal', href: '/gem-portal' },
  { id: 4, label: 'Advantages', href: '/advantages' },
  { id: 5, label: 'Resources',  href: '/resources' },
  { id: 6, label: 'Dealers',    href: '/dealers', disabled: true },
  { id: 7, label: 'Contact Us',     href: '/contact' },
  { id: 8, label: 'Other Products', href: '/other-products', disabled: true },
];

export const DEF_OTHER_PRODUCTS = [
  {
    id: 'cold-zinc',
    name: 'Cold Zinc Galvanizing',
    category: 'Corrosion Protection',
    badge: 'Bestseller',
    badgeColor: '#16a34a',
    image: '',
    gradientFrom: '#1e3a5f',
    gradientTo: '#2563eb',
    desc: 'ZINGA® is a single-component, cold-applied zinc-rich coating that provides cathodic protection to steel — identical in performance to hot-dip galvanizing. It can be applied on-site without any special equipment.',
    specs: [
      { label: 'Zinc content',  value: '96% in dry film', bold: true },
      { label: 'Thickness',     value: '30–40 µm DFT' },
      { label: 'Coverage',      value: '~7 m²/litre @ 30µm' },
      { label: 'Standards',     value: 'ISO 12944, SSPC' },
    ],
    features: ['Cathodic protection', 'Single component', 'On-site application', 'Recoatable & repairable'],
  },
  {
    id: 'zinc-primer',
    name: 'Zinc Phosphate Primer',
    category: 'Protective Coatings',
    badge: '',
    badgeColor: '#2563eb',
    image: '',
    gradientFrom: '#0f4c3a',
    gradientTo: '#10b981',
    desc: 'High-performance zinc phosphate primer for structural steel, pipelines and industrial equipment, providing an anti-corrosive base coat with excellent adhesion to all standard topcoats.',
    specs: [
      { label: 'Type',         value: 'Epoxy/Alkyd ZP', bold: true },
      { label: 'VOC',          value: '< 250 g/litre' },
      { label: 'Dry time',     value: '4 h touch / 16 h overcoat' },
      { label: 'Substrate',    value: 'Steel St 2 / Sa 2' },
    ],
    features: ['Excellent adhesion', 'Fast drying', 'Compatible with most topcoats', 'Marine grade'],
  },
  {
    id: 'epoxy-coating',
    name: 'Epoxy Coating System',
    category: 'Industrial Coatings',
    badge: 'Industrial Grade',
    badgeColor: '#7c3aed',
    image: '',
    gradientFrom: '#3b0764',
    gradientTo: '#7c3aed',
    desc: 'Two-component epoxy systems for heavy-duty industrial environments — chemical plants, water/wastewater, marine structures and offshore platforms. Available in high-build and self-priming variants.',
    specs: [
      { label: 'Components',   value: '2-pack epoxy', bold: true },
      { label: 'DFT',         value: '100–250 µm' },
      { label: 'Chemical res.', value: 'pH 3–11' },
      { label: 'Temp. resist.',value: 'Up to 120°C' },
    ],
    features: ['Chemical resistant', 'High build', 'Excellent durability', 'Low VOC options'],
  },
  {
    id: 'pu-topcoat',
    name: 'Polyurethane Topcoats',
    category: 'Decorative & Protective',
    badge: '',
    badgeColor: '#d97706',
    image: '',
    gradientFrom: '#92400e',
    gradientTo: '#f59e0b',
    desc: 'Aliphatic PU topcoats delivering exceptional gloss, colour retention and UV resistance for structural steel, bridges, and architectural metalwork exposed to harsh environments.',
    specs: [
      { label: 'Gloss level',  value: '> 85 GU @ 60°', bold: true },
      { label: 'UV resistance',value: 'Aliphatic – excellent' },
      { label: 'Colours',      value: 'RAL / custom' },
      { label: 'Recoat',       value: '6 h @ 20°C' },
    ],
    features: ['UV resistant', 'Wide colour range', 'Gloss retention > 3 yrs', 'Weather resistant'],
  },
  {
    id: 'surface-prep',
    name: 'Surface Preparation Products',
    category: 'Surface Treatment',
    badge: '',
    badgeColor: '#0891b2',
    image: '',
    gradientFrom: '#0c4a6e',
    gradientTo: '#0891b2',
    desc: 'A complete range of degreasing, phosphating, rust conversion and flash-rust inhibition products to ensure optimal adhesion and longevity of any coating system.',
    specs: [
      { label: 'Products',     value: 'Degreaser, Phosphate, RC', bold: true },
      { label: 'Substrate',    value: 'Steel, GI, Aluminium' },
      { label: 'Method',       value: 'Brush / spray / dip' },
      { label: 'Standards',    value: 'SSPC-SP11, ISO 8501' },
    ],
    features: ['Degreaser', 'Phosphate treatment', 'Rust converter', 'Flash-rust inhibitor'],
  },
  {
    id: 'intumescent',
    name: 'Intumescent Fire Protection',
    category: 'Fire Protection',
    badge: 'CE Marked',
    badgeColor: '#dc2626',
    image: '',
    gradientFrom: '#7f1d1d',
    gradientTo: '#dc2626',
    desc: 'Water-based and solvent-based intumescent coatings for passive fire protection of structural steel columns and beams, providing up to 120 minutes fire resistance per EN 13381.',
    specs: [
      { label: 'Fire rating',  value: 'R30 – R120', bold: true },
      { label: 'Standard',     value: 'EN 13381-8' },
      { label: 'Finish',       value: 'Paintable / coloured' },
      { label: 'Application',  value: 'Brush / airless spray' },
    ],
    features: ['Up to 120 min FR', 'Thin film', 'Paintable finish', 'CE & ETA certified'],
  },
];

export const DEF_SETTINGS = {
  companyName:    'John Galt Zinga Technologies',
  tagline:        'All India Distributor of ZINGA\u00ae Film Galvanization',
  phone:          '+91-9372134903',
  altPhone:       '+91-9820325285',
  email:          'zinga@johngalt.in',
  altEmail:       'sales.zinga@johngalt.in',
  hqAddress:      '208, Makhija Arcade, 35th Road, Off Linking Road, Bandra (W), Mumbai 400050',
  hqPhone:        '+91-22-26003255',
  godownAddress:  'Plot F-2, MIDC Badlapur, 421503, Dist. Thane, India',
  godownPhone:    '+91-251-2690120',
  gst:            '27AAEHR6718L1ZN',
  whatsapp:       'https://api.whatsapp.com/send?phone=919820325285',
  whatsappPhone:  '919820325285',
  whatsappEnabled: true,
  linkedin:       'https://www.linkedin.com/company/13422501/',
  facebook:       'https://www.facebook.com/johngaltzinga/',
};

export const DEF_STATS = [
  { id: 1, value: '45+',    label: 'Years Experience' },
  { id: 2, value: '15,000', label: 'Sqm Plant Area' },
  { id: 3, value: '1000+',  label: 'Projects Completed' },
  { id: 4, value: '500+',   label: 'Happy Clients' },
];

export const DEF_FOOTER = {
  companyDesc:      'ZINGA® is an anti-corrosive, single-component, high-performance metallic coating with 96% zinc in dry film. Authorised All India Distributor of ZingaMetall BvBa.',
  copyrightName:    'John Galt Zinga Technologies',
  extLink1Label:    'John Galt International',
  extLink1Url:      'https://www.johngalt.in',
  extLink2Label:    'ZingaMetall BvBa',
  extLink2Url:      'https://www.zinga.be',
  showProducts:     true,
  showQuickLinks:   true,
};

export const DEF_GEM = [
  {
    id: 1,
    name: 'Zinga - 1 kg Packing',
    description: 'Grey Colour, 96% Zinc in Dry film thickness, 99.995% Zinc Purity. Will be in 1 kg packing',
    tdsLink: 'https://www.zinga.be/en/products/zinga',
    gemProductId: '5116877-96844560344',
    catalogId: '5116877-96844560344-cat',
    gemLink: 'https://mkp.gem.gov.in',
  },
  {
    id: 2,
    name: 'Zinga - 5 kg Packing',
    description: 'Grey Colour, 96% Zinc in Dry film thickness, 99.995% Zinc Purity. Will be in 5 kg packing',
    tdsLink: 'https://www.zinga.be/en/products/zinga',
    gemProductId: '5116877-80897400312',
    catalogId: '5116877-80897400312-cat',
    gemLink: 'https://mkp.gem.gov.in',
  },
  {
    id: 3,
    name: 'Zingaalu 5 kg',
    description: 'Zingaalu - Film Galvanizing with Hot Dip Finish (silver) - 5kg',
    tdsLink: 'https://www.zinga.be/en/products/zingalu',
    gemProductId: '5116877-60981682453',
    catalogId: '5116877-60981682453-cat',
    gemLink: 'https://mkp.gem.gov.in',
  },
  {
    id: 4,
    name: 'Zingaspray',
    description: 'Aerosol Spray - Grey colour finish. 500 ml packing. Life is 1 year',
    tdsLink: 'https://www.zinga.be/en/products/zingaspray',
    gemProductId: '5116877-7727510191',
    catalogId: '5116877-7727510191-cat',
    gemLink: 'https://mkp.gem.gov.in',
  },
];

export const DEF_ABOUT = {
  hero: {
    badge:    'About Us',
    title:    'John Galt International',
    subtitle: 'Leaders in heavy engineering and galvanizing solutions — crafting quality since 1978.',
    stats: [
      { id: 1, value: '1978',      label: 'Founded' },
      { id: 2, value: '150+',      label: 'Employees' },
      { id: 3, value: 'TQM',       label: 'Certified' },
      { id: 4, value: 'Pan-India', label: 'Distributor' },
    ],
  },
  intro: {
    tag:       'Our Company',
    heading:   'About John Galt International',
    paragraph: 'John Galt International is a Total Quality Management certified heavy engineering company with over 45 years of experience in designing, manufacturing, and supplying process equipment to industries across India and abroad.',
    highlights: [
      'Designed to international ASME & IS standards',
      'Full-cycle engineering: design, fabrication, installation',
      'Serving petroleum, chemical, pharma & food sectors',
      'Trusted by leading Indian and global companies',
    ],
  },
  mission: {
    heading: 'Our Mission',
    text:    'To provide world-class engineering solutions with uncompromising quality, innovation, and customer satisfaction — in every aspect of our operations.',
  },
  vision: {
    heading: 'Our Vision',
    text:    'To be the most trusted heavy engineering brand in India — known for precision, reliability, and lasting partnerships with our clients.',
  },
  expertise: {
    tag:     'What We Build',
    heading: 'Our Core Expertise',
    items: [
      { id: 1, title: 'Storage Tanks & Bullets',         desc: 'Custom-designed tanks and pressure bullets for petroleum, chemical, and allied industries.' },
      { id: 2, title: 'Pressure Vessels & Reactors',     desc: 'High-pressure equipment meeting ASME, IS, and international fabrication standards.' },
      { id: 3, title: 'Heat Exchangers',                  desc: 'Shell-and-tube, plate, and air-cooled exchangers for efficient thermal management.' },
      { id: 4, title: 'Distillation Columns',             desc: 'Precision-engineered columns for petrochemical, pharmaceutical, and food industries.' },
      { id: 5, title: 'Industrial Boilers',               desc: 'Fire-tube and water-tube boilers designed for reliability across industrial applications.' },
      { id: 6, title: 'Material Handling Equipment',      desc: 'E.O.T. cranes, stackers, conveyors, and structural frames for heavy-duty operations.' },
    ],
  },
  jgzt: {
    tag:     'Part of the Group',
    heading: 'John Galt Zinga Technologies',
    cards: [
      { id: 1, tag: 'Principal Importer', heading: 'Authorised All-India Distributor',   text: 'John Galt Zinga Technologies is the principal importer and authorised all-India distributor of ZINGA® products, bringing Belgium\'s best cold galvanizing technology to every corner of the country.' },
      { id: 2, tag: 'Market Leader',      heading: '30+ Years of Cold Galvanizing',       text: 'ZINGA® is India\'s market leader in cold galvanizing with a proven track record of protecting structures, pipelines, and infrastructure across the country for over three decades.' },
      { id: 3, tag: 'Belgian Heritage',   heading: 'ZingaMetall BvBa, Belgium',           text: 'The ZINGA® formulation originates from ZingaMetall BvBa, Belgium — a globally recognised corrosion-protection innovator whose products comply with the most stringent international quality and environmental standards.' },
    ],
  },
};

export const DEF_HOW_IT_WORKS = {
  eyebrow: 'Science of Protection',
  heading: 'How does it work?',
  para1: 'When two different metals come in contact with each other in presence of an electrolyte (e.g. water), they form a galvanic cell in which the lesser noble metal (e.g. Zn) corrodes in favour of the more noble metal (e.g. steel). This electrochemical reaction is the base for the complex field that is cathodic protection.',
  para2: 'Galvanic, cathodic protection, or active protection, arises from zinc (the anode) sacrificing itself in favour of the base metal -steel (the cathode) with the resulting flow of electrons preventing steel corrosion. In this way the protection of the metal is guaranteed, even when the zinc layer is slightly damaged.',
  para3: 'Other well-established methods of cathodic protection include hot-dip galvanising (HDG) and zinc thermal spraying, both of which exhibit a constant sacrificial rate of the zinc layer.',
  paraBottom: 'With ZINGA the sacrificial rate reduces dramatically after the zinc layer has oxidised and the natural porosity has been filled with zinc salts. Additionally the zinc particles within the ZINGA layer are protected by the organic binder without adversely affecting the electrical conductivity. This enables ZINGA to create nearly the same galvanic potential between the zinc and the steel as hot dip galvanising but with a lower rate of zinc loss because, put simply, the binder acts as a "corrosion inhibitor" to the zinc.',
};

export const DEF_COMPANY_TRUST = {
  jgi: {
    heading: 'About John Galt International',
    para1: 'Founded in 1978, John Galt International is a Total Quality Management certified heavy engineering company.',
    para2: 'Our expertise lies in design, manufacturing and supply of process equipment such as',
    bullets: [
      'Storage tanks and bullets, Pressure Vessels and Reactors, Heat Exchangers, Distillation Columns, Boilers',
      'Material Handling Equipment such as E.O.T Cranes, Stackers, Reclaimers and Wagon Tipplers, Conveyors and Frames',
    ],
    para3: 'We have a 15000 sq meter manufacturing plant in Badlapur, Maharashtra',
    websiteLabel: 'Click here to access our website',
    websiteUrl: 'https://www.johngalt.in',
  },
  jgzt: {
    heading: 'About John Galt Zinga Technologies',
    para1: 'John Galt Zinga Technologies is the authorised all India Distributor of ZingaMetall BvBa. We are the principal importers of Zinga products in India.',
    para2: 'Zinga is the market leader in Cold Galvanizing.',
  },
  team: {
    heading: 'Team',
    members: [
      { id: 1, name: 'R.K. BHATIA',  role: 'Founding Partner', bio: 'Mr. R.K. Bhatia is a graduate in Mechanical Engineering and post graduation in Business Management. He has more than 45 years of Industrial experience, starting his career with companies like Larsen & Toubro and Voltas. He has received many recognitions from Industrial Forums for his contribution in the field of manufacturing. He manages operations and finances for the group.', initials: 'RKB', cardBg: '#4a4a5a' },
      { id: 2, name: 'ABHAY BHATIA', role: 'Partner',          bio: 'Abhay graduated in Computer Engineering in 2007, following which he worked in various roles at John Galt. In 2013, he completed his MBA in General Management and Marketing from the Indian School of Business. Since then, Abhay heads sales, marketing and strategy.', initials: 'AB', cardBg: '#1a4b7a' },
    ],
  },
};

export const DEF_CONTACT_CTA = {
  newsletter: {
    heading:     'Subscribe to newsletter',
    placeholder: 'Enter your email here*',
    btnText:     'Subscribe',
    successMsg:  'Thank you for subscribing!',
  },
  cta: {
    heading: 'Experience Unparalleled Corrosion Protection with ZINGA!',
    btnText: 'SHOP NOW',
    btnLink: '/products',
    image:   'https://static.wixstatic.com/media/4c89a3_8977ab9f4607449bbb69bbce437f203c~mv2.jpg/v1/fill/w_500,h_350,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/Zinga.jpg',
  },
};

export const DEF_HOME_SHOWCASE = [
  {
    id: 'zinga', name: 'Zinga',
    subtitle: '1 pack coating containing 96% zinc and providing cathodic protection',
    image: 'https://static.wixstatic.com/media/4c89a3_8977ab9f4607449bbb69bbce437f203c~mv2.jpg/v1/fill/w_500,h_350,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/Zinga.jpg',
    specs: [
      { label: 'Zinc Content', value: '96%(±1%) in Dry Film (provides cathodic protection)', bold: true },
      { label: 'Zinc Purity', value: '99.995% pure', bold: true },
      { label: 'Colour', value: 'Steel Grey' },
      { label: 'Atmospheric temperature resistance', value: '-40 Degree C to 150 Degree C', bold: true },
      { label: 'Shelf Life', value: 'Unlimited', bold: true },
      { label: 'Coverage', value: '3.62 m2 / kg OR 9.67 m2 / litre with 60 micron thickness', bold: true },
      { label: 'Packaging', value: '1 kg and 5 kg', bold: true },
    ],
  },
  {
    id: 'zingalu', name: 'Zingalu',
    subtitle: 'A 1 component high performance standalone organic zinc rich coating',
    image: 'https://static.wixstatic.com/media/ab29c6_1cfbeb841d98435c83bf5cab53c0979f~mv2.jpeg/v1/fill/w_500,h_350,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/WhatsApp%20Image%202021-12-24%20at%209_35_52%20AM.jpeg',
    specs: [
      { label: 'Zinc Content', value: '90% ultra pure Zinc + 4% Aluminum Flakes in Dry Film', bold: true },
      { label: 'Zinc Purity', value: '99.995% pure', bold: true },
      { label: 'Colour', value: 'Grey with Aluminum Shine', bold: true },
      { label: 'Shelf Life', value: '2 years in original unopened package', bold: true },
      { label: 'Coverage', value: '4.26 m2 / kg OR 10.5 m2 / litre with 60 micron thickness', bold: true },
      { label: 'Packaging', value: '5 kg', bold: true },
    ],
  },
  {
    id: 'aquazinga', name: 'AquaZinga',
    subtitle: 'A 2 pack 100% water based anti-corrosive system based on inorganic zinc silicates.',
    image: 'https://static.wixstatic.com/media/4c89a3_eda7113dde4d438bac6f28350a1dc838~mv2.jpg/v1/fill/w_500,h_350,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/AQUAZINGA.jpg',
    specs: [
      { label: 'Zinc Content', value: '92% (±2%) in Dry Film (provides cathodic protection)', bold: true },
      { label: 'Colour', value: 'Steel Grey' },
      { label: 'Atmospheric temperature resistance', value: '-90 Degree C to 600 Degree C', bold: true },
      { label: 'Shelf Life', value: '12 months if unopened', bold: true },
      { label: 'Coverage', value: '3.62 m2 / kg OR 9.67 m2 / litre for 60 microns', bold: true },
      { label: 'Packaging', value: '10 kg - 7.6 kg powder and 2.4 kg binder', bold: true },
    ],
  },
  {
    id: 'zingatarfree', name: 'Zinga Tarfree - Topcoat',
    subtitle: 'A 1 pack moisture curing polyurethane paint with excellent water and corrosion resistance.',
    image: 'https://static.wixstatic.com/media/4c89a3_b8a9797979ea4521b38e1ff94b8957b9~mv2.jpg/v1/fill/w_500,h_350,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/ZINGATARFREE.jpg',
    specs: [
      { label: 'Colour', value: 'Black' },
      { label: 'Shelf Life', value: '2 years if unopened', bold: true },
      { label: 'UV Resistance', value: 'Excellent', bold: true },
      { label: 'Coverage', value: 'For 90 microns, 7.6 square meter per litre', bold: true },
      { label: 'Packaging', value: '5 litre' },
    ],
  },
];

export const DEF_HOME_VIDEOS = [
  {
    id: 1,
    title: 'ZINGA Product Overview',
    url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
  },
  {
    id: 2,
    title: 'Cold Galvanizing Demo',
    url: 'https://www.youtube.com/watch?v=aqz-KE-bpKQ',
  },
  {
    id: 3,
    title: 'Application Walkthrough',
    url: 'https://www.youtube.com/watch?v=9No-FiEInLA',
  },
];
